import { useEffect, useState, useMemo } from "react";
import api, { dkk, num } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Search, Package } from "lucide-react";

export function ProductStats({ store, dateParams }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const [sortKey, setSortKey] = useState("revenue");

  useEffect(() => {
    if (!dateParams) return;
    let active = true;
    setLoading(true);
    api.get("/kpi/products", { params: { ...dateParams, store, limit: 1000 } })
      .then(({ data }) => { if (active) setRows(data); })
      .catch(() => { if (active) setRows([]); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [store, dateParams]);

  const cats = useMemo(
    () => ["all", ...Array.from(new Set(rows.map((r) => r.category))).sort()],
    [rows]
  );
  const filtered = useMemo(() => {
    let list = rows;
    if (cat !== "all") list = list.filter((r) => r.category === cat);
    if (q.trim()) list = list.filter((r) => r.product.toLowerCase().includes(q.toLowerCase()));
    return [...list].sort((a, b) => (b[sortKey] || 0) - (a[sortKey] || 0));
  }, [rows, cat, q, sortKey]);

  const totUnits = filtered.reduce((s, r) => s + (r.units || 0), 0);
  const totRev = filtered.reduce((s, r) => s + (r.revenue || 0), 0);
  const totProfit = filtered.reduce((s, r) => s + (r.profit || 0), 0);

  return (
    <div className="rounded-2xl bg-[#ffffff] border border-[#e2e8f0] p-4 sm:p-5" data-testid="product-stats">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <h3 className="text-sm font-semibold uppercase tracking-[0.14em] text-slate-500 flex items-center gap-2">
          <Package className="w-4 h-4" /> Produktstatistik
        </h3>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 sm:flex-none">
            <Search className="w-4 h-4 text-slate-500 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Søg vare…"
              className="h-9 pl-8 w-full sm:w-56 rounded-lg bg-[#f8fafc] border-[#e2e8f0] text-slate-800"
              data-testid="product-search"
            />
          </div>
          <select
            value={cat}
            onChange={(e) => setCat(e.target.value)}
            className="h-9 rounded-lg bg-[#f8fafc] border border-[#e2e8f0] text-slate-700 text-sm px-2"
            data-testid="product-category-filter"
          >
            {cats.map((c) => (
              <option key={c} value={c}>{c === "all" ? "Alle kategorier" : c}</option>
            ))}
          </select>
        </div>
      </div>

      {loading ? (
        <p className="text-slate-500 text-sm py-8 text-center">Henter…</p>
      ) : filtered.length === 0 ? (
        <p className="text-slate-500 text-sm py-8 text-center" data-testid="product-stats-empty">
          Ingen synkroniserede varedata for perioden endnu.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[560px]" data-testid="product-stats-table">
            <thead>
              <tr className="text-slate-500 text-xs uppercase tracking-wider border-b border-[#e2e8f0]">
                <th className="text-left font-medium py-2 pr-2 w-8">#</th>
                <th className="text-left font-medium py-2 pr-2">Produkt</th>
                <th className="text-left font-medium py-2 pr-2">Kategori</th>
                <th className={`text-right font-medium py-2 pr-2 cursor-pointer select-none ${sortKey === "units" ? "text-[#38BDF8]" : ""}`} onClick={() => setSortKey("units")} data-testid="sort-units">Antal solgt</th>
                <th className={`text-right font-medium py-2 pr-2 cursor-pointer select-none ${sortKey === "revenue" ? "text-[#38BDF8]" : ""}`} onClick={() => setSortKey("revenue")} data-testid="sort-revenue">Omsætning</th>
                <th className={`text-right font-medium py-2 pr-2 cursor-pointer select-none ${sortKey === "profit" ? "text-[#38BDF8]" : ""}`} onClick={() => setSortKey("profit")} data-testid="sort-profit">Fortjeneste</th>
                <th className="text-right font-medium py-2 pr-2">Margin</th>
                <th className="text-right font-medium py-2">Gns. pris</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r, i) => (
                <tr key={r.product + i} className="border-b border-[#e2e8f0] hover:bg-[#f8fafc]/60" data-testid={`product-row-${i}`}>
                  <td className="py-2 pr-2 text-slate-500 tabular-nums">{i + 1}</td>
                  <td className="py-2 pr-2 text-slate-700">{r.product}</td>
                  <td className="py-2 pr-2 text-slate-500">{r.category}</td>
                  <td className="py-2 pr-2 text-right tabular-nums text-slate-800">{num(r.units)}</td>
                  <td className="py-2 pr-2 text-right tabular-nums text-slate-800">{dkk(r.revenue)}</td>
                  <td className="py-2 pr-2 text-right tabular-nums" style={{ color: (r.profit || 0) < 0 ? "#F87171" : "#34D399" }}>{dkk(r.profit)}</td>
                  <td className="py-2 pr-2 text-right tabular-nums text-slate-500">{r.margin != null ? `${Math.round(r.margin)}%` : "—"}</td>
                  <td className="py-2 text-right tabular-nums text-slate-500">{r.units ? dkk(r.revenue / r.units) : "—"}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="text-slate-500 font-medium border-t border-[#e2e8f0]">
                <td></td>
                <td className="py-2.5">{filtered.length} varer</td>
                <td></td>
                <td className="py-2.5 text-right tabular-nums">{num(totUnits)}</td>
                <td className="py-2.5 text-right tabular-nums">{dkk(totRev)}</td>
                <td className="py-2.5 text-right tabular-nums" style={{ color: totProfit < 0 ? "#F87171" : "#34D399" }}>{dkk(totProfit)}</td>
                <td></td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}
